'use client';
import _extends from '@babel/runtime/helpers/esm/extends';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { forwardRef, useState, useRef, useContext, useEffect } from 'react';
import { TextInputWrapper, TextInputContent } from '@paypalcorp/pp-react-text-input';
import { IntlContext, getFormattedMessage } from '@paypalcorp/pp-react-intl';
import { remToPx, cx } from '@paypalcorp/pp-react-utils';
import * as styles from './styles.css';
export { styles as textAreaStyles };

var _excluded = ["className", "minRows", "maxRows", "showCharacterCounter", "maxCharacters", "counterStatusText", "onChange", "onBlur", "onFocus", "value", "errorText", "successText"],
  _excluded2 = ["inputClassName", "name", "id", "setFocus", "hasError", "placeholder", "label", "labelClassName", "inputTag", "placemat"];

// A new requirement was added to allow 1 as a min rows
// The default should still be 2 rows.
var MIN_ROWS = 1;
var DEFAULT_ROWS = 2;
var MAX_ROWS = 6;
var TextArea = forwardRef(function (_ref, ref) {
  var className = _ref.className,
    minRows = _ref.minRows,
    maxRows = _ref.maxRows,
    _ref$showCharacterCou = _ref.showCharacterCounter,
    showCharacterCounter = _ref$showCharacterCou === void 0 ? false : _ref$showCharacterCou,
    maxCharacters = _ref.maxCharacters,
    counterStatusText = _ref.counterStatusText,
    _ref$onChange = _ref.onChange,
    onChange = _ref$onChange === void 0 ? null : _ref$onChange,
    _ref$onBlur = _ref.onBlur,
    onBlur = _ref$onBlur === void 0 ? null : _ref$onBlur,
    _ref$onFocus = _ref.onFocus,
    onFocus = _ref$onFocus === void 0 ? null : _ref$onFocus,
    value = _ref.value,
    errorText = _ref.errorText,
    successText = _ref.successText,
    rest = _objectWithoutProperties(_ref, _excluded);
  var textLineHeight = remToPx(1.5);
  var maxAllowedRows = maxRows > MIN_ROWS ? Math.min(maxRows, MAX_ROWS) : MAX_ROWS;
  var minAllowedRows = minRows >= MIN_ROWS ? Math.max(minRows, MIN_ROWS) : DEFAULT_ROWS;
  var _useState = useState(value ? value.length : 0),
    _useState2 = _slicedToArray(_useState, 2),
    charCount = _useState2[0],
    setCharCount = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isRTL = _useState4[0],
    setRTL = _useState4[1];
  var internalRef = useRef();
  var textAreaRef = ref || internalRef;
  var intlContext = useContext(IntlContext);
  var bordersOffset = useRef(0);
  var maxHeight = useRef(textLineHeight * maxAllowedRows);
  var minHeight = useRef(textLineHeight * minAllowedRows);
  var computeHeight = function () {
    var textAreaElm = textAreaRef.current;
    // reset height to min height before setting it again
    textAreaElm.style.height = "".concat(minHeight.current, "px");
    var newHeight = textAreaElm.scrollHeight + bordersOffset.current;
    /* istanbul ignore next */
    if (newHeight > maxHeight.current) {
      textAreaElm.style.height = "".concat(maxHeight.current, "px");
    } else if (newHeight > minHeight.current) {
      textAreaElm.style.height = "".concat(newHeight, "px");
    } else {
      textAreaElm.style.height = "".concat(minHeight.current, "px");
    }
  };
  useEffect(function () {
    /* istanbul ignore next */
    if (textAreaRef.current) {
      var textAreaElm = textAreaRef.current;
      var offsetHeight = textAreaElm.offsetHeight,
        clientHeight = textAreaElm.clientHeight;
      var computedStyle = window.getComputedStyle(textAreaElm);
      var lineHeightValue = Number(computedStyle.getPropertyValue('line-height').split('px')[0]);
      var paddingBottomValue = Number(computedStyle.getPropertyValue('padding-bottom').split('px')[0]);
      var paddingTopValue = Number(computedStyle.getPropertyValue('padding-top').split('px')[0]);
      var lineHeight = Number.isNaN(lineHeightValue) ? textLineHeight : lineHeightValue;
      var paddingBottom = Number.isNaN(paddingBottomValue) ? 0 : paddingBottomValue;
      var paddingTop = Number.isNaN(paddingTopValue) ? 0 : paddingTopValue;
      bordersOffset.current = offsetHeight - clientHeight;
      maxHeight.current = bordersOffset.current + paddingTop + paddingBottom + lineHeight * maxAllowedRows;
      minHeight.current = bordersOffset.current + paddingTop + paddingBottom + lineHeight * minAllowedRows;
      computeHeight();
      setRTL(computedStyle.direction === 'rtl');
    }
  }, [minAllowedRows, maxAllowedRows, value]);
  var handleCounterWrapperClick = function () {
    /* istanbul ignore else */
    if (textAreaRef.current) {
      var textAreaElm = textAreaRef.current;
      textAreaElm.focus();
    }
  };
  return /*#__PURE__*/React.createElement(TextInputWrapper, _extends({
    className: className,
    value: value,
    onChange: function handleChange(event) {
      var textAreaElm = event.target;
      setCharCount(textAreaElm.value.length);
      /* istanbul ignore else */
      if (onChange) {
        onChange(event);
      }
    },
    onFocus: function handleFocus(event) {
      /* istanbul ignore else */
      if (onFocus) {
        onFocus(event);
      }
    },
    onBlur: function handleBlur(event) {
      /* istanbul ignore else */
      if (onBlur) {
        onBlur(event);
      }
    },
    renderContent: function renderContent(_ref2) {
      var inputClassName = _ref2.inputClassName,
        name = _ref2.name,
        id = _ref2.id,
        setFocus = _ref2.setFocus,
        hasError = _ref2.hasError,
        placeholder = _ref2.placeholder,
        label = _ref2.label,
        labelClassName = _ref2.labelClassName;
        _ref2.inputTag;
        _ref2.placemat;
        var otherProps = _objectWithoutProperties(_ref2, _excluded2);
      var textInputContent = /*#__PURE__*/React.createElement(TextInputContent, _extends({
        inputClassName: cx(inputClassName, styles.textAreaControl),
        inputTag: "textarea",
        name: name,
        id: id,
        setFocus: setFocus,
        inputRef: textAreaRef,
        hasError: hasError,
        placeholder: placeholder,
        label: label,
        labelClassName: cx(labelClassName, styles.textAreaControlLabel, styles.textAreaControlErrorLabel)
      }, otherProps, {
        "data-ppui": "true"
      }));
      var wrapperClasses = cx(styles.textAreaWrapper, hasError && styles.textAreaErrorCounterWrapper);
      if (showCharacterCounter) {
        var maxCharsText = '';
        if (maxCharacters) {
          maxCharsText = isRTL ? "".concat(maxCharacters, "/") : "/".concat(maxCharacters);
        }
        var counterText = isRTL ? "".concat(maxCharsText).concat(charCount) : "".concat(charCount).concat(maxCharsText);
        var counterStatusMessage = getFormattedMessage(intlContext, "".concat(charCount, " out of ").concat(maxCharacters, " characters used"), counterStatusText, {
          charCount: charCount,
          maxCharacters: maxCharacters
        });
        var counterTextClasses = cx(styles.textAreaCounterText, hasError && styles.textAreaErrorCounterText);
        return /*#__PURE__*/React.createElement("div", {
          className: wrapperClasses,
          onClick: handleCounterWrapperClick,
          "data-ppui": "true"
        }, textInputContent, /*#__PURE__*/React.createElement("div", {
          className: counterTextClasses,
          role: "status",
          "aria-live": "polite",
          "aria-atomic": "true",
          "aria-label": counterStatusMessage,
          "data-ppui": "true"
        }, counterText));
      }
      return /*#__PURE__*/React.createElement("div", {
        className: wrapperClasses,
        onClick: handleCounterWrapperClick,
        "data-ppui": "true"
      }, textInputContent);
    },
    errorText: errorText,
    successText: successText,
    "data-ppui-info": "text-area_1.2.3__8.12.2-beta.0"
  }, rest));
});

export { TextArea };
